import {
  FIND_LOG_UPLOAD_MAX_RETRIES,
  FIND_LOG_UPLOAD_TIMEOUT_MS,
} from '../../config/findRemoteLogging';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let lastUploadError = null;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getFindRemoteLogUploadDiagnostics() {
  return lastUploadError ? { ...lastUploadError } : null;
}

export function recordFindRemoteLogUploadFailure(mode, status, body, batchSize = 0) {
  lastUploadError = {
    at: Date.now(),
    mode,
    status,
    body: body?.slice?.(0, 500) ?? body ?? null,
    batchSize,
  };
  console.warn('[findRemoteLog] upload failed', lastUploadError); // not app_logs: see sink.js
}

function clearUploadError() {
  lastUploadError = null;
}

async function readResponseBody(response) {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

async function postLogBatch(url, rows, accessToken, prefer) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FIND_LOG_UPLOAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: prefer,
      },
      body: JSON.stringify(rows),
      signal: controller.signal,
    });
    const body = await readResponseBody(response);
    return { ok: response.ok, status: response.status, body };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, status: 0, body: message };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload log rows via raw fetch — bypasses supabaseFetch / fetchOutcomes / TanStack offline queue.
 */
export async function uploadFindLogBatch(rows, accessToken, { table = 'lynx_find_logs' } = {}) {
  if (!supabaseUrl || !supabaseAnonKey || !accessToken || rows.length === 0) {
    return { ok: false, status: 0, mode: 'precheck' };
  }

  // Also carries app_logs (lib/appLog): same shape, same unique (session_id, seq), same own-rows RLS.
  const baseUrl = `${supabaseUrl}/rest/v1/${table}`;
  let lastStatus = 0;
  let lastMode = 'unknown';

  for (let attempt = 0; attempt <= FIND_LOG_UPLOAD_MAX_RETRIES; attempt += 1) {
    if (attempt > 0) {
      await sleep(400 * attempt);
    }

    // Prefer plain insert — works when all rows are new.
    lastMode = 'plain';
    let result = await postLogBatch(baseUrl, rows, accessToken, 'return=minimal');
    lastStatus = result.status;
    if (result.ok) {
      clearUploadError();
      return { ok: true, status: result.status, mode: lastMode, uploadedCount: rows.length };
    }

    // Retry with ignore-duplicates when unique (session_id, seq) rows already landed.
    if (result.status === 409 || result.status === 400) {
      lastMode = 'ignore-duplicates';
      result = await postLogBatch(
        `${baseUrl}?on_conflict=session_id,seq`,
        rows,
        accessToken,
        'return=minimal,resolution=ignore-duplicates',
      );
      lastStatus = result.status;
      if (result.ok) {
        clearUploadError();
        return { ok: true, status: result.status, mode: lastMode, uploadedCount: rows.length };
      }
    }

    if (result.status >= 400 && result.status < 500 && result.status !== 408) {
      recordFindRemoteLogUploadFailure(lastMode, result.status, result.body, rows.length);
      return { ok: false, status: result.status, mode: lastMode, body: result.body };
    }

    recordFindRemoteLogUploadFailure(lastMode, result.status, result.body, rows.length);
  }

  return { ok: false, status: lastStatus, mode: lastMode };
}
